/**
 * WS-HF — the Higgsfield model catalogue, written ONCE.
 *
 * There are two executors and they do NOT share a catalogue:
 *
 *  - **`cloud`** — the REST API the platform holds a key for. Verified in the owner's console:
 *    `xai/grok-imagine-image-2.0` and `marketing-studio/image` take a reference photo, plus four
 *    TEXT-TO-IMAGE models that do not.
 *  - **`device`** — the desktop app talking MCP to the operator's own subscription:
 *    `nano_banana_pro`, `nano_banana_2`, `gpt_image_2_5`, `gpt_image_2` and `flux_3_image`.
 *
 * Every screen that lets somebody pick a model, or tells them which model is about to run, reads
 * this table. Two lists would be two chances to offer a model the running path cannot execute —
 * which the operator only discovers after the vendor has taken the credits.
 *
 * **A model that cannot read a reference photo is listed and NEVER selectable.** Every generation
 * this product runs is image-to-image: the case job sends three photographs, and the single-stage
 * path sends the design plus the template's case photo. A text-to-image model would accept the
 * request, ignore the pictures, bill the credits and return a case nobody asked for. So the four
 * are rendered as disabled options carrying the reason, rather than left off the screen where the
 * operator would go looking for them in the console instead.
 */

/** Who runs the generation. Mirrors `enqueueGeneration`'s `executor` and the case-job field. */
export type HfExecutor = "cloud" | "device";

export interface HiggsfieldModel {
  /**
   * The wire id, sent verbatim and never passed through a label formatter.
   *
   * For a `referenceCapable: false` entry this is a DISPLAY key only — nothing selectable
   * carries it, so it never reaches the vendor.
   */
  id: string;
  /** What the operator reads. Equals `id` for the models whose id is already the product name. */
  label: string;
  executor: HfExecutor;
  /** Values the `resolution` param accepts, in ascending order. */
  resolutions: string[];
  /** Values the `quality` param accepts. EMPTY when the model has no quality param at all. */
  qualities: string[];
  /**
   * Whether the model reads the reference photos this product always sends. `false` means
   * text-to-image: listed, captioned and never selectable.
   */
  referenceCapable: boolean;
  /**
   * The price of one square image, in credits, keyed `resolution` — or `resolution/quality`
   * for a model with a quality param. Live quotes of 2026-10-04 (`docs/higgsfield-mcp.md`
   * "Models and their cost"; the cloud's `KNOWN_MAX_CREDITS`); `gpt_image_2_5` on Sunburst.
   * A key not listed (any `4k`) has no recorded price.
   */
  listCredits?: Record<string, number>;
  /** Runs on the company subscription only: a desktop older than its media role pays for an
   *  image that ignores the pictures. */
  subscriptionOnly?: boolean;
}

/** The resolution and quality a run generates at — the one setting in Admin → AI providers. */
export interface HfFrame {
  resolution: string;
  quality: string;
}

/** What runs when nothing is saved, or when an older cloud names none. */
export const DEFAULT_HF_FRAME: HfFrame = { resolution: "1k", quality: "high" };
/** The values the one setting offers. `gpt_image_2_5` also takes `xhigh`/`max`; never quoted. */
export const HF_RESOLUTIONS = ["1k", "2k", "4k"];
export const HF_QUALITIES = ["low", "medium", "high"];

export const HIGGSFIELD_MODELS: readonly HiggsfieldModel[] = [
  // --- cloud / REST API (the platform's key), image-to-image ---
  {
    id: "xai/grok-imagine-image-2.0",
    label: "xai/grok-imagine-image-2.0",
    executor: "cloud",
    resolutions: ["1k", "2k"],
    qualities: ["low", "medium"],
    referenceCapable: true,
  },
  {
    id: "marketing-studio/image",
    label: "marketing-studio/image",
    executor: "cloud",
    resolutions: ["1k", "2k", "4k"],
    qualities: ["low", "medium", "high"],
    referenceCapable: true,
  },
  // --- cloud / REST API, TEXT-TO-IMAGE: listed, captioned, never selectable ---
  { id: "soul-2", label: "Soul 2", executor: "cloud", resolutions: [], qualities: [], referenceCapable: false },
  { id: "soul-standard", label: "Soul Standard", executor: "cloud", resolutions: [], qualities: [], referenceCapable: false },
  { id: "qwen-image-3", label: "Qwen Image 3", executor: "cloud", resolutions: [], qualities: [], referenceCapable: false },
  { id: "z-image-turbo", label: "Z-Image Turbo", executor: "cloud", resolutions: [], qualities: [], referenceCapable: false },
  // --- device / MCP (the operator's subscription) ---
  {
    id: "nano_banana_pro",
    label: "nano_banana_pro",
    executor: "device",
    listCredits: { "1k": 2, "2k": 2 },
    resolutions: ["1k", "2k", "4k"],
    qualities: [],
    referenceCapable: true,
  },
  {
    id: "nano_banana_2",
    label: "nano_banana_2",
    executor: "device",
    listCredits: { "1k": 1.5, "2k": 2 },
    resolutions: ["1k", "2k", "4k"],
    qualities: [],
    referenceCapable: true,
  },
  {
    id: "gpt_image_2_5",
    // Always run on its Sunburst variant (the cloud's `MODEL_PARAMS`, the desktop's
    // `with_model_params`).
    label: "gpt_image_2_5 (Sunburst)",
    executor: "device",
    listCredits: {
      "1k/low": 0.25, "2k/low": 0.5, "1k/medium": 0.5, "2k/medium": 1, "1k/high": 1.5, "2k/high": 2.75,
    },
    resolutions: ["1k", "2k", "4k"],
    qualities: ["low", "medium", "high"],
    referenceCapable: true,
  },
  {
    id: "gpt_image_2",
    label: "gpt_image_2",
    executor: "device",
    subscriptionOnly: true,
    listCredits: {
      "1k/low": 1, "2k/low": 1, "1k/medium": 1, "2k/medium": 2, "1k/high": 3.5, "2k/high": 6.5,
    },
    resolutions: ["1k", "2k", "4k"],
    qualities: ["low", "medium", "high"],
    referenceCapable: true,
  },
  {
    id: "flux_3_image",
    label: "flux_3_image",
    executor: "device",
    listCredits: { "1k": 2, "2k": 3 },
    resolutions: ["768sq", "1k", "1.5k", "2k", "4k"],
    qualities: [],
    referenceCapable: true,
  },
];

/**
 * The caption above each group in a model picker. The words say WHOSE money runs the model,
 * because that is the only difference the operator can act on.
 */
export const HIGGSFIELD_GROUP_LABELS: Record<HfExecutor, string> = {
  cloud: "API (server key)",
  device: "My subscription (MCP, desktop)",
};

/** The sentence a text-to-image entry carries, in its option and in its tooltip. */
export const TEXT_TO_IMAGE_NOTE = "text-to-image; no reference photo";

/** The provider-level default — the API's image-to-image edit model. */
export const DEFAULT_HIGGSFIELD_MODEL = "xai/grok-imagine-image-2.0";

/**
 * The model each case-job stage runs when nothing is stored.
 *
 * On the device path the two stages differ on purpose: stage 1 draws the whole product from three
 * photographs and gets the stronger model, stage 2 only repaints a panel on an already-approved
 * base and does not need it. On the API path one edit model serves both.
 */
export const CASE_JOB_MODEL_DEFAULTS: Record<CaseWallet, { clear: string; magenta: string }> = {
  cloud: { clear: "xai/grok-imagine-image-2.0", magenta: "xai/grok-imagine-image-2.0" },
  device: { clear: "nano_banana_pro", magenta: "nano_banana_2" },
  // The company subscription runs over MCP, so it reaches the canvas models a device does
  // (`cloud/app/models/case_jobs.py` SUBSCRIPTION_*_MODEL).
  subscription: { clear: "nano_banana_pro", magenta: "nano_banana_2" },
};

/**
 * WS-HF shared login — WHOSE money a case job spends, which is also which model pair it runs:
 * the platform's API key (`cloud`), a desktop's own subscription (`device`), or the ONE company
 * subscription an admin connected (`subscription`, run by the cloud worker). The keys are the
 * cloud's `case_job_models` keys.
 */
export type CaseWallet = HfExecutor | "subscription";

/** What a person reads for each wallet — on the stage card, the confirm, and the admin table. */
export const CASE_WALLET_LABELS: Record<CaseWallet, string> = {
  ...HIGGSFIELD_GROUP_LABELS,
  subscription: "Company Higgsfield subscription",
};

/** The catalogue a wallet can run. The company subscription speaks MCP, like a desktop. */
export function walletCatalogue(wallet: CaseWallet): HfExecutor {
  return wallet === "subscription" ? "device" : wallet;
}

/** Every model one executor can run, in catalogue order — including the unselectable ones. */
export function modelsFor(executor: HfExecutor): HiggsfieldModel[] {
  return HIGGSFIELD_MODELS.filter((m) => m.executor === executor);
}

/** Only the models a case job or a single-stage generation on `wallet` may actually be given. */
export function selectableModelsFor(wallet: CaseWallet): HiggsfieldModel[] {
  return modelsFor(walletCatalogue(wallet))
    .filter((m) => m.referenceCapable && !(wallet === "device" && m.subscriptionOnly));
}

/** The square frame one MCP run asks for: each field only where the model takes the value. */
export function frameParams(model: string, frame: HfFrame): Record<string, unknown> {
  const params: Record<string, unknown> = { aspect_ratio: "1:1" };
  if (resolutionsFor(model).includes(frame.resolution)) params.resolution = frame.resolution;
  if (qualitiesFor(model).includes(frame.quality)) params.quality = frame.quality;
  return params;
}

/** The recorded price of exactly what `params` sends to `id`, or `undefined` when none is. */
export function listCreditsFor(id: string, params: Record<string, unknown>): number | undefined {
  const model = higgsfieldModel(id);
  if (!model?.listCredits || (params.variant ?? "sunburst") !== "sunburst") return undefined;
  const key = model.qualities.length ? `${params.resolution}/${params.quality}` : `${params.resolution}`;
  return model.listCredits[key];
}

/** The sentence for a run with no quote and no recorded price: nothing bounds its cost. */
export function noQuoteRefusal(id: string): string {
  return `higgsfield_no_quote: no cost quote came back, and ${id} has no recorded price at this size and quality. Nothing was sent.`;
}

/** The sentence for a run whose quote or recorded price is above the per-run ceiling. */
export function overCeilingRefusal(credits: number, ceiling: number): string {
  return `higgsfield_quote_over_ceiling: this run would cost ${credits} credits, above the ${ceiling}-credit ceiling for one run. Nothing was sent.`;
}

/** One model by id, or `undefined` for an id this build does not know. */
export function higgsfieldModel(id: string): HiggsfieldModel | undefined {
  return HIGGSFIELD_MODELS.find((m) => m.id === id);
}

/**
 * The resolutions a model accepts. An UNKNOWN id answers an empty list rather than a guessed
 * one: offering `4k` for a model that refuses it is a refusal the operator pays for.
 */
export function resolutionsFor(id: string): string[] {
  return higgsfieldModel(id)?.resolutions ?? [];
}

/** The qualities a model accepts; empty when it has no quality param. */
export function qualitiesFor(id: string): string[] {
  return higgsfieldModel(id)?.qualities ?? [];
}
